// Layout atoms — thin, token-only shared layout primitives from the design kit
// (Card, Divider, Eyebrow, Kbd). Presentational wrappers, no logic.
import type { CSSProperties, ReactNode } from 'react';

export interface CardProps {
  children: ReactNode;
  style?: CSSProperties;
}

// Padded, elevated surface container.
export function Card({ children, style }: CardProps) {
  return (
    <div
      style={{
        padding: 'var(--s-5)',
        background: 'var(--bg-1)',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-2)',
        boxShadow: 'var(--shadow-1)',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

export interface DividerProps {
  orientation?: 'horizontal' | 'vertical';
  style?: CSSProperties;
}

// Thin separator rule. Horizontal by default.
export function Divider({ orientation = 'horizontal', style }: DividerProps) {
  const vertical = orientation === 'vertical';
  return (
    <div
      role="separator"
      aria-orientation={orientation}
      style={
        vertical
          ? { width: 0, alignSelf: 'stretch', borderLeft: '1px solid var(--border-1)', ...style }
          : { height: 0, width: '100%', borderTop: '1px solid var(--border-1)', ...style }
      }
    />
  );
}

export interface EyebrowProps {
  children: ReactNode;
  style?: CSSProperties;
}

// Uppercase section label — wraps the global `.eyebrow` class
// (styles/colors_and_type.css); styling is NOT duplicated here.
export function Eyebrow({ children, style }: EyebrowProps) {
  return (
    <div className="eyebrow" style={style}>
      {children}
    </div>
  );
}

export interface KbdProps {
  children: ReactNode;
  style?: CSSProperties;
}

// Keyboard-shortcut badge.
export function Kbd({ children, style }: KbdProps) {
  return (
    <kbd
      style={{
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--fz-11)',
        padding: 'var(--s-1) var(--s-2)',
        border: '1px solid var(--border-1)',
        borderRadius: 'var(--r-1)',
        background: 'var(--bg-2)',
        color: 'var(--fg-2)',
        ...style,
      }}
    >
      {children}
    </kbd>
  );
}
